import { precedentConsensus, precedentTitleConsensus } from "./precedentConsensus";
import { historyFor, leadHistory } from "./historySignalStore";

/**
 * 출처별 세번(2026-09-28) — 관세율표 명칭·국내 결정례·관세사 신고이력이 각각 가리키는 세번을 나란히 둔다.
 * 새로 추측하지 않는다: 카드가 이미 받은 데이터만 읽는다. 같은 세번(또는 같은 소호·호)을 가리키면 묶어 보이고,
 * 다르면 사용자가 줄을 눌러 지도에서 확인한다. 판정은 트리가 한다(표시·길 안내일 뿐).
 *
 * 반환 { rows: [{ kind, code, level, ... }], agreement: { code, level, kinds } | null }
 *  - level: 10(세번)·6(소호)·4(호)
 *  - agreement: 두 출처 이상이 같은 자리를 가리키는 가장 깊은 자리(10 → 6 → 4)
 */
export function sourceCodes({ tariffMatches = [], headingMatches = [], precedentHints = null, brokerHistory = null, precise = null, importer = null } = {}) {
  const rows = [];

  // 관세율표 — 10자리 품명 일치가 먼저, 없으면 호·소호 명칭 일치.
  const leaf = (Array.isArray(tariffMatches) ? tariffMatches : []).find((m) => digits(m?.hsk).length === 10);
  if (leaf) {
    const others = new Set((tariffMatches || []).map((m) => digits(m?.hsk)).filter((h) => h.length === 10 && h !== digits(leaf.hsk)));
    rows.push({ kind: "tariff", code: digits(leaf.hsk), level: 10, name: leaf.ko || leaf.en || "",
      via: leaf.nameVia === "llm" || leaf.llm ? "llm" : leaf.modifier ? "modifier" : "name", more: others.size });
  } else {
    const heads = (Array.isArray(headingMatches) ? headingMatches : [])
      .map((h) => ({ code: digits(h?.code), text: String(h?.line_ko || h?.line_en || "").replace(/^[\d.\s]+/, "").trim() }))
      .filter((h) => h.code.length === 4 || h.code.length === 6);
    if (heads.length) {
      const first = heads.slice().sort((a, b) => a.code.length - b.code.length)[0];
      rows.push({ kind: "tariff", code: first.code, level: first.code.length, name: first.text, via: "heading",
        also: heads.filter((h) => h !== first).map((h) => h.code) });
    }
  }

  // 국내 결정례 — 합의(3건·80%↑) 또는 제목 일치, 없으면 건수가 가장 많은 세번.
  const consensus = precedentConsensus(precedentHints, []) || precedentTitleConsensus(precedentHints);
  const byHsk = Array.isArray(precedentHints?.by_hsk) ? precedentHints.by_hsk.filter((r) => digits(r?.hsk).length === 10 && Number(r?.count) > 0) : [];
  const topRow = consensus ? byHsk.find((r) => digits(r.hsk) === consensus.hsk) || null
    : byHsk.slice().sort((a, b) => Number(b.count) - Number(a.count))[0] || null;
  if (consensus || topRow) {
    const code = consensus ? consensus.hsk : digits(topRow.hsk);
    const total = Number(precedentHints?.eligible_total ?? byHsk.reduce((s, r) => s + Number(r.count), 0)) || 0;
    const cnt = Number(topRow?.count || consensus?.count || 0);
    // 약한 근거(2026-09-29): 합의·제목 일치가 아니고 '최다'인데 절반도 안 되면 일치 판단에 넣지 않는다(제2165호 실측 — 13·37번이 잡음끼리 일치).
    const weak = !consensus && !(total > 0 && cnt / total >= 0.5);
    // 호 자리에서는 그 호 전체의 결정례 비율로 본다(같은 호의 여러 세번으로 나뉘어도 호는 모일 수 있다).
    const hs4Count = byHsk.filter((r) => digits(r.hsk).startsWith(code.slice(0, 4))).reduce((s2, r) => s2 + Number(r.count), 0);
    const weakHeading = !consensus && !(total > 0 && hs4Count / total >= 0.5);
    rows.push({ kind: "precedent", code, level: 10, count: cnt, total, weak, weakHeading,
      year: String(topRow?.latest_date || consensus?.latest_date || "").slice(0, 4),
      basis: consensus ? (consensus.title ? "title" : "consensus") : "most",
      label: String(topRow?.label || ""), excerpt: excerpt(topRow?.desc) });
  }

  // 관세사 신고이력 — 1위 세번(표본 비율)과 그 호의 합계.
  const top = brokerHistory?.top;
  if (top && digits(top.hsk).length === 10) {
    const code = digits(top.hsk);
    const heading = historyFor(brokerHistory, code.slice(0, 4));
    // 관세사 이력은 강한·앞선 신호(과반·2위의 두 배·같은 물품 근거 10건+)일 때만 일치 판단에 넣는다.
    // 호 자리에서는 그 호의 이력 합계가 과반이고 10건 이상이면 강한 것으로 본다(Shawls: 6214.20 41% 이지만 6214 호 83%).
    const weakHeading = !(heading && heading.share >= 50 && heading.observations >= 10) || Boolean(brokerHistory.broad);
    rows.push({ kind: "history", code, level: 10, weak: !leadHistory(brokerHistory), weakHeading, share: Number(top.share) || 0, observations: Number(top.observations) || 0,
      headingShare: heading ? heading.share : null, headingObservations: heading ? heading.observations : null,
      sampled: Boolean(brokerHistory.sampled), partial: Boolean(brokerHistory.partial), broad: Boolean(brokerHistory.broad) });
  }

  // PROVE 학습 모델(2026-09-29) — 결정례 정답지·관세사 신고이력으로 학습한 모델의 세번 제안(결정례 300건 10자리 81%·봉인 전 측정).
  // 다른 출처와 같은 무게로 일치 판단에 넣는다. 모델 이름은 보이지 않는다.
  const mp = precedentHints?.model_proposal;
  if (mp && digits(mp.hsk).length === 10) {
    rows.push({ kind: "model", code: digits(mp.hsk), level: 10, ...(mp.research ? { research: true } : {}) });
  }

  // 정밀 검토(2026-10-05) — 한글 서술형에서 PROVE·LLM 후보가 엇갈릴 때만 정밀 모델이 호 하나를 낸다(호 수준, 일치 판단에 넣는다).
  if (precise && precise.status === "ok" && digits(precise.hs4).length === 4) {
    rows.push({ kind: "precise", code: digits(precise.hs4), level: 4, reason: String(precise.reason || "") });
  }

  // 같은 수입자 과거 신고(2026-10-06) — 사무소의 관행이지 정답이 아니다. 모델과 호가 같고 과거 신고가 갈리지 않았을 때만
  // 일치 판단에 넣는다(그 밖엔 '재검토 권장'으로 보이기만). 학습 모델 답이 없으면 혼자서는 길을 정하지 않는다.
  // 엄격 층(2026-11-04 세번 라우터 R1): 같은 수입자+같은 거래품명 3건↑·1위 80%↑. 시험 450문항에서 모델과 10자리가 같으면 99.5%라
  // 약한 줄로 내리지 않는다 — 그래도 혼자서는 길을 정하지 않고(두 출처 규칙), 모델과 다르면 재검토 권장으로 보인다.
  // 순서(2026-11-04 실측): 비슷한 품목 글(규격·모델까지 같은 반복 수입)이 갈림 없이 답하면 그것이 먼저 — 450문항에서 답 71.8%·6자리 99.1%로
  // 엄격 층(52.0%·97.9%)보다 넓고 정확했다. 엄격 층은 비슷한 품목이 없거나 갈릴 때(검색어가 거래품명뿐일 때 등) 대신 쓴다.
  const similarOk = importer && importer.status === "ok" && digits(importer.top_hsk).length === 10 && !importer.split;
  const strict = !similarOk && importer?.strict?.status === "ok" && digits(importer.strict.hsk).length === 10 ? importer.strict : null;
  if (strict) {
    const rel = String(strict.relation || "");
    rows.push({ kind: "importer", code: digits(strict.hsk), level: 10, weak: false, weakHeading: false, strict: true,
      supplierMatch: Boolean(strict.supplier_match), count: Number(strict.count) || 0, total: Number(strict.total) || 0, last: "",
      relation: rel, split: false, review: rel === "differ", votes: [], matches: [] });
  } else if (importer && importer.status === "ok" && digits(importer.top_hsk).length === 10) {
    const rel = String(importer.relation || "");
    const trusted = !importer.split && ["agree10", "agree6", "agree4"].includes(rel);
    rows.push({ kind: "importer", code: digits(importer.top_hsk), level: 10,
      weak: !(trusted && rel === "agree10"), weakHeading: !trusted,
      count: Number(importer.top_count) || 0, total: Number(importer.total_count) || 0, last: String(importer.last_date || ""),
      relation: rel, split: Boolean(importer.split), review: Boolean(importer.review),
      votes: Array.isArray(importer.votes) ? importer.votes : [], matches: Array.isArray(importer.matches) ? importer.matches : [] });
  }

  // 과거 신고 세번이 올해 관세율표에 없으면(폐지·변경, 서버가 세번 마스터로 확인) 줄을 만들지 않고 상태 줄에만 알린다.
  const expired = similarOk || strict ? ""
    : importer?.strict?.status === "expired" ? digits(importer.strict.expired_hsk)
    : importer?.status === "expired" ? digits(importer.expired_hsk) : "";
  const status = strict ? "ok" : expired ? "expired" : importer?.status;
  return { rows, agreement: agreementOf(rows), precisePending: Boolean(precise && precise.status === "pending"),
    importer: importer && importer.status !== "idle" ? { status, code: importer.importer || "", ...(expired ? { expiredHsk: expired } : {}) } : null };
}

/** 두 출처 이상이 같은 자리를 가리키는 가장 깊은 자리. 같은 깊이에 여럿이면 출처가 많은 쪽. */
export function agreementOf(rows) {
  for (const level of [10, 6, 4]) {
    const groups = new Map();
    for (const r of rows || []) {
      if (!r || String(r.code).length < level) continue;
      if (level === 4 ? (r.weakHeading ?? r.weak) : r.weak) continue;   // 약한 근거는 그 자리의 일치 판단에 넣지 않는다
      const key = String(r.code).slice(0, level);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r.kind);
    }
    const best = [...groups.entries()].filter(([, kinds]) => new Set(kinds).size >= 2).sort((a, b) => b[1].length - a[1].length)[0];
    if (best) return { code: best[0], level, kinds: [...new Set(best[1])] };
  }
  return null;
}

export const SOURCE_LABEL = { tariff: "관세율표", precedent: "결정례", history: "관세사 이력", model: "PROVE 모델", precise: "정밀 검토", importer: "같은 수입자" };

function digits(v) { return String(v || "").replace(/\D/g, ""); }

/** 결정례 성상 원문의 앞부분 한 구절(표시용). */
function excerpt(desc) {
  // 원문의 가운뎃점이 '?'로 깨져 적재된 것(성상?규격)을 되돌린다.
  const s = String(desc || "").replace(/(?<=[가-힣])\?(?=[가-힣])/gu, "·")
    .replace(/[ㅇ○•·]\s*성상[^\s]*\s*/u, "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  const cut = s.split(/\s*[ㅇ○•]\s*원산지|\s*ㅇ\s/u)[0];
  return cut.length > 70 ? `${cut.slice(0, 70)}…` : cut;
}

/** 지도에 없을 수 있는 출처의 호(관세율표 명칭·관세사 이력) — 지도가 그 호의 소호·국내 세번까지 점선으로 그린다. */
export function sourceHeadings(sources) {
  const out = [];
  for (const r of Array.isArray(sources?.rows) ? sources.rows : []) {
    // 결정례의 호도 넣는다 — 빠지면 출처별 세번의 결정례 줄을 눌러도 지도에 갈 마디가 없다(2026-09-28 금 합금 바 → 8505).
    const hs4 = String(r.code || "").slice(0, 4);
    const why = r.kind === "importer" ? "같은 수입자 과거 신고의 호" : r.kind === "history" ? "관세사 이력 1위의 호" : r.kind === "precedent" ? "국내 결정례의 호" : r.kind === "model" ? "PROVE 모델 제안의 호" : "관세율표 명칭 일치";
    if (hs4.length === 4 && !out.some((x) => x.hs4 === hs4)) out.push({ hs4, why });
  }
  return out;
}
